/**
 * The player's arms, on foot: a punch with either fist, and something
 * picked up off the ground held in both hands, raised or lowered, swung to
 * either side and let go.
 *
 * A punch is a hook: the elbow comes up out to the side, bent square, the
 * body turned away to load it; then the body turns hard into it and the
 * fist sweeps round across the front at shoulder height, and comes back
 * down. About 0.4 s, landing (`PUNCH.lands`) as it comes across in front. A swing turns the body from one
 * side to the other with both arms out holding what they hold, and lands
 * halfway, straight ahead. Each laid over the walk (`gait.ts`), so the legs
 * walk on as the arms work.
 *
 * Pure: `main.ts` reads the hands from the drawn body and does what a hit
 * does.
 */

import type { GaitPose } from './gait.js';

export type Side = 'left' | 'right';

/** A punch's length, and when in it the fist lands, seconds. */
export const PUNCH = { time: 0.42, lands: 0.17 };
/** A swing's length, and when in it it lands (straight ahead), seconds. */
export const SWING = { time: 0.5, lands: 0.25 };

export interface Arms {
  /** Seconds into each fist's punch, NaN with none thrown. */
  punch: Record<Side, number>;
  /** Something in both hands. */
  holding: boolean;
  /** How high it is held: 0 down at the hips, 1 over the head. */
  lift: number;
  /** Seconds into a swing, NaN with none; and to which side it swings. */
  swing: number;
  swingTo: Side;
}

export function newArms(): Arms {
  return { punch: { left: NaN, right: NaN }, holding: false, lift: 0.35, swing: NaN, swingTo: 'left' };
}

/** Throw a punch with `side`'s fist, unless that fist is already busy or the hands are full. */
export function punch(a: Arms, side: Side): boolean {
  if (a.holding || !Number.isNaN(a.punch[side])) return false;
  a.punch[side] = 0;
  return true;
}

/** Swing what is held round to `side`, unless a swing is going. */
export function swing(a: Arms, side: Side): boolean {
  if (!a.holding || !Number.isNaN(a.swing)) return false;
  a.swing = 0;
  a.swingTo = side;
  return true;
}

/** Both hands to a thing: no punch goes on. */
export function takeHold(a: Arms): void {
  a.holding = true;
  a.punch.left = a.punch.right = NaN;
  a.swing = NaN;
}

export function letGo(a: Arms): void {
  a.holding = false;
  a.swing = NaN;
}

/** Raise (positive) or lower what is held by `by`, of the way from the hips to over the head. */
export function raise(a: Arms, by: number): void {
  a.lift = Math.max(0, Math.min(1, a.lift + by));
}

/** On by `dt`: what landed in it, a fist or the swing. */
export function stepArms(a: Arms, dt: number): Array<Side | 'swing'> {
  const landed: Array<Side | 'swing'> = [];
  for (const side of ['left', 'right'] as const) {
    const t = a.punch[side];
    if (Number.isNaN(t)) continue;
    if (t < PUNCH.lands && t + dt >= PUNCH.lands) landed.push(side);
    a.punch[side] = t + dt >= PUNCH.time ? NaN : t + dt;
  }
  if (!Number.isNaN(a.swing)) {
    if (a.swing < SWING.lands && a.swing + dt >= SWING.lands) landed.push('swing');
    a.swing = a.swing + dt >= SWING.time ? NaN : a.swing + dt;
  }
  return landed;
}

const smooth = (x: number) => {
  const t = Math.max(0, Math.min(1, x));
  return t * t * (3 - 2 * t);
};
const mix = (a: number, b: number, t: number) => a + (b - a) * t;

/**
 * How far round a punch `t` seconds in has the fist, 0 (out to the side,
 * or hanging) to 1 (across the front), and how far it is loaded first,
 * the elbow up and the body turned away, 0..1.
 */
export function reachOf(t: number): { out: number; cocked: number } {
  if (Number.isNaN(t)) return { out: 0, cocked: 0 };
  const u = t / PUNCH.time;
  const lands = PUNCH.lands / PUNCH.time;
  const out = u < lands ? smooth((u - 0.15) / (lands - 0.15)) : 1 - smooth((u - lands - 0.12) / (1 - lands - 0.12));
  const cocked = u < 0.15 ? smooth(u / 0.15) : 1 - smooth((u - 0.15) / 0.2);
  return { out, cocked };
}

/** `base` (a walk) with the arms doing what `a` says. */
export function armPose(base: GaitPose, a: Arms): GaitPose {
  const pose: GaitPose = { ...base, shoulder: { ...base.shoulder }, elbow: { ...base.elbow } };
  if (a.holding) {
    // Both arms out to what is held, higher the higher it is; the elbows
    // bent under it low down, straighter overhead.
    const shoulder = mix(0.35, 2.8, a.lift), elbow = mix(0.9, 0.25, a.lift);
    pose.shoulder.left = pose.shoulder.right = shoulder;
    pose.elbow.left = pose.elbow.right = elbow;
    pose.spread = 0.1;
    if (!Number.isNaN(a.swing)) {
      // Round from the other side to this, through straight ahead.
      const u = smooth(a.swing / SWING.time);
      pose.twist += (a.swingTo === 'left' ? 1 : -1) * -Math.cos(Math.PI * u) * 0.95;
    }
    return pose;
  }
  for (const side of ['left', 'right'] as const) {
    const { out, cocked } = reachOf(a.punch[side]);
    if (out === 0 && cocked === 0) continue;
    // The upper arm up level out to the side, the elbow bent square so the
    // fist is ahead of it; swung round from beside the body to across the
    // front, the body turning away to load it and then hard into it.
    const up = Math.max(out, cocked);
    pose.armOut ??= { left: 0, right: 0 };
    pose.armOut[side] = (1.45 - pose.spread) * up;
    pose.shoulder[side] = mix(pose.shoulder[side], mix(0.15, 1.15, out), up);
    pose.elbow[side] = mix(pose.elbow[side], 1.75, up);
    pose.twist += (side === 'left' ? 1 : -1) * (0.35 * cocked - 0.65 * out);
  }
  return pose;
}
